import {
  buildVideoSrc,
  buildVideoPosterSrc,
  buildCustomPosterSrc,
} from './build-video-delivery-urls';
import type { VideoTransformations } from './build-video-delivery-urls';

// Boolean <video> attributes arrive from the editor as the strings
// 'true' / 'false' (select editors), or as real booleans from calling
// components. They must become real booleans on the element.
const BOOLEAN_VIDEO_ATTRIBUTES = new Set(['controls', 'autoplay', 'loop', 'muted', 'playsinline']);

// React prop names for attributes whose DOM names are all-lowercase.
const REACT_PROP_NAMES: { [key: string]: string } = {
  autoplay: 'autoPlay',
  playsinline: 'playsInline',
};

const coerceBoolean = (value: any): boolean => value === true || value === 'true';

export function RVideo({
  cloudName = 'rulecms',
  publicId = 'main-sample-video',
  transformations,
  componentAttributes,
  styleProps = {},
  className,
}: Readonly<{
  cloudName?: string;
  publicId?: string;
  // Extra delivery transformations merged over the defaults (q_auto,vc_auto,c_limit,w_1920).
  // Object form { c: 'fill', ar: '16:9' } or raw string form 'c_fill,ar_16:9'.
  transformations?: VideoTransformations | string;
  componentAttributes?: { [key: string]: any };
  styleProps?: { [key: string]: any };
  /**
   * Host-app classes for this column, when the renderer decided this card
   * carries them. Concatenated after any pass-through-video-attributes-className.
   */
  className?: string;
}>) {
  // Priority: video-selector-3-resolutions > video-public-id > default publicId prop
  const userCloudName = componentAttributes?.['video-host-cloud-name'];
  const selectorPublicId = componentAttributes?.['video-selector-3-resolutions'];
  const userPublicId = componentAttributes?.['video-public-id'];

  const finalCloudName = userCloudName || cloudName;
  const finalPublicId = selectorPublicId || userPublicId || publicId;

  // Editor-authored transformations (per-device, flattened by the widget
  // pipeline) take precedence over the prop from the calling component
  const componentTransformations = componentAttributes?.['video-transformations-3-resolutions'];
  const finalTransformations = componentTransformations || transformations;

  // Optional custom poster (an uploaded image's public ID); falls back to the
  // video's own first frame so there is always a poster.
  const posterPublicId = componentAttributes?.['video-poster-selector-3-resolutions'];

  // Extract pass-through props for the video element
  // Convention:
  // - 'pass-through-video-attributes-style-' prefix for style properties
  // - 'pass-through-video-attributes-' prefix for other HTML attributes
  const videoAttributes: { [key: string]: any } = {
    // Classic content-video defaults: visible controls, inline playback on
    // mobile (no forced fullscreen), and metadata-only preload so dropping a
    // video on a page does not download the whole file.
    controls: true,
    playsInline: true,
    preload: 'metadata',
    poster: posterPublicId
      ? buildCustomPosterSrc(finalCloudName, posterPublicId)
      : buildVideoPosterSrc(finalCloudName, finalPublicId),
  };

  const videoStyles: { [key: string]: any } = {
    // A replaced element with a large intrinsic size should shrink to its
    // container, not overflow it; height follows to keep the aspect ratio.
    maxWidth: '100%',
    height: 'auto',
  };

  if (componentAttributes) {
    Object.keys(componentAttributes).forEach((keyWithDimensionSuffix) => {
      const value = componentAttributes[keyWithDimensionSuffix];

      // Unset attributes arrive as undefined entries (the device-type
      // flattening keeps the key); skip them so defaults are not clobbered.
      if (value === undefined || value === null) {
        return;
      }

      // Remove the -3-resolutions suffix if it exists
      const key = keyWithDimensionSuffix.endsWith('-3-resolutions')
        ? keyWithDimensionSuffix.slice(0, -'-3-resolutions'.length)
        : keyWithDimensionSuffix;

      // Handle style attributes
      if (key.startsWith('pass-through-video-attributes-style-')) {
        const styleProp = key.slice('pass-through-video-attributes-style-'.length);
        videoStyles[styleProp] = value;
      }

      // Handle other HTML attributes (controls, autoplay, poster, width, etc.)
      else if (key.startsWith('pass-through-video-attributes-')) {
        const attributeName = key.slice('pass-through-video-attributes-'.length);

        if (BOOLEAN_VIDEO_ATTRIBUTES.has(attributeName)) {
          const reactName = REACT_PROP_NAMES[attributeName] || attributeName;
          if (coerceBoolean(value)) {
            videoAttributes[reactName] = true;
          } else {
            // 'false' must remove the attribute (booleans are presence-based),
            // including the built-in controls/playsInline defaults.
            delete videoAttributes[reactName];
          }
          return;
        }

        videoAttributes[REACT_PROP_NAMES[attributeName] || attributeName] = value;
      }
    });
  }

  // Column className comes last so host utilities can override pass-through
  // classes from the editor. Omit the attribute entirely when neither is set.
  const { className: passThroughClassName, ...restVideoAttributes } = videoAttributes;
  const mergedClassName = [passThroughClassName, className].filter(Boolean).join(' ') || undefined;

  // Plain <video> with delivery URLs built directly: renders complete markup
  // (src + poster) on the server. Progressive MP4 keeps the element simple
  // and universally playable.
  return (
    <video
      src={buildVideoSrc(finalCloudName, finalPublicId, finalTransformations)}
      {...restVideoAttributes}
      {...(mergedClassName !== undefined ? { className: mergedClassName } : {})}
      style={{ ...videoStyles, ...styleProps }}
    />
  );
}
